import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import debounce from 'debounce-promise';
import ReactSelect from 'react-select';
import { Input } from 'react-select/animated';
import Async from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';
import { styled, css } from 'styled-components';
import { Media } from '../../theme/Media';
// CustomInput adds `name` to the `react-select`s main input to support focus operations by name (for final-form-focus)
const CustomInput = (props) => {
    if (!Input) {
        return null;
    }
    const { selectProps } = props;
    return (_jsx(Input, { ...props, "data-testid": selectProps.dataTestId, name: selectProps.name, readOnly: selectProps.readOnly ?? false, autoComplete: 'off' }));
};
const commonProps = {
    classNamePrefix: 'select',
    menuPlacement: 'auto',
    components: {
        Input: CustomInput,
    },
};
// Disabling our codestyle constraints as we are styling a third-party lib here
/* stylelint-disable selector-class-pattern, media-query-no-invalid */
const styles = css `
  & .select__control {
    border-radius: 4px;
    border-color: ${({ theme }) => theme.colors.grey30_1};
    background-color: ${({ theme }) => theme.colors.grey00_7};
    &--is-disabled {
      background-color: ${({ theme }) => theme.colors.grey05_4};
      border-color: ${({ theme }) => theme.colors.grey30_2};
      cursor: not-allowed;
    }
    &:hover {
      border-color: ${({ theme }) => theme.colors.grey70_1};
      cursor: pointer;
    }
  }
  & .select__control.select__control--is-focused {
    border: 1px solid ${({ theme }) => theme.colors.main};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.main};
  }
  & .select__indicators .select__indicator-separator {
    display: none;
  }
  & .select__indicator.select__dropdown-indicator {
    svg {
      fill: ${({ theme }) => theme.colors.main};
    }
  }

  & .select__value-container {
    & .select__input-container {
      color: ${({ theme }) => theme.colors.black_1};
    }
    & .select__single-value {
      color: ${({ theme }) => theme.colors.black_1};
      &--is-disabled {
        color: ${({ theme }) => theme.colors.grey100_2};
      }
    }
    .campaign-page & {
      height: auto;
      @media ${Media.smMin} {
        height: 30px;
      }
    }

    & .select__multi-value {
      background: ${({ theme }) => theme.colors.grey05_2};
      .select__multi-value__label {
        color: ${({ theme }) => theme.colors.black_1};
      }
      .select__multi-value__remove {
        svg > path:not([fill='transparent']) {
          fill: ${({ theme }) => theme.colors.grey100_1};
        }
        &:hover {
          background: none;
          cursor: pointer;
          svg > path:not([fill='transparent']) {
            fill: ${({ theme }) => theme.colors.black_1};
          }
        }
      }
      &--is-disabled {
        background: ${({ theme }) => theme.colors.grey01_2};
        border-color: transparent;
        .select__multi-value__label {
          color: ${({ theme }) => theme.colors.grey70_3};
        }
        .select__multi-value__remove {
          svg > path:not([fill='transparent']) {
            fill: ${({ theme }) => theme.colors.grey70_3};
          }
        }
      }
    }
  }
  & .select__option {
    @supports (-ms-ime-align: auto) {
      word-break: break-all;
    }
  }
  & .select__menu-notice--no-options {
    color: ${({ theme }) => theme.colors.grey100_2};
  }
  & .select__placeholder {
    color: ${({ theme }) => theme.colors.grey70_1};
  }
  & .select__option.select__option--is-selected {
    background: ${({ theme }) => theme.colors.grey05_1};
    color: ${({ theme }) => theme.colors.black_1};
  }
  & .select__option.select__option--is-focused.select__option--is-selected {
    background: ${({ theme }) => theme.colors.grey05_1};
  }
  & .select__option.select__option--is-focused {
    background: ${({ theme }) => theme.colors.grey05_1};
  }
  & .select__option:hover {
    background: ${({ theme }) => theme.colors.grey05_1};
  }

  & .select__menu {
    z-index: 2;
    ${({ theme }) => theme.shadows.large}
    background-color: ${({ theme }) => theme.colors.grey00_6};
    & .select__menu-list {
      background-color: ${({ theme }) => theme.colors.grey00_6};
    }
  }
  &.select--is-disabled {
    .select__indicator.select__dropdown-indicator {
      svg > path:not([fill='transparent']) {
        fill: ${({ theme }) => theme.colors.grey30_1};
      }
    }
  }
`;
const StyledSelect = styled(ReactSelect) `
  ${styles};
`;
const SAsyncSelect = styled(Async) `
  ${styles};
`;
const SCreatableSelect = styled(CreatableSelect) `
  ${styles};
`;
const SAsyncCreatableSelect = styled(AsyncCreatableSelect) `
  ${styles};
`;
const Select = ({ innerRef, formatOptionLabel, ...props }) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const customFilter = React.useCallback((option, searchText) => (formatOptionLabel?.(option, {
        context: 'value',
        inputValue: '',
        selectValue: [option],
    })?.toString() ?? option.label)
        .toLowerCase()
        .includes(searchText.toLowerCase()), [formatOptionLabel]);
    return (_jsx(StyledSelect, { filterOption: customFilter, ref: innerRef, formatOptionLabel: formatOptionLabel, ...commonProps, ...props }));
};
const StyledAsyncCreatableSelect = ({ innerRef, loadOptions, ...props }) => (_jsx(SAsyncCreatableSelect, { ref: innerRef, loadOptions: React.useMemo(() => (loadOptions ? debounce(loadOptions, 300) : undefined), [loadOptions]), ...commonProps, ...props }));
const StyledCreatableSelect = ({ innerRef, ...props }) => (_jsx(SCreatableSelect
// @ts-expect-error ts cannot infer the correct type here
, { 
    // @ts-expect-error ts cannot infer the correct type here
    ref: innerRef, ...commonProps, ...props }));
const StyledAsyncSelect = ({ innerRef, loadOptions, ...props }) => (_jsx(SAsyncSelect, { ref: innerRef, loadOptions: React.useMemo(() => (loadOptions ? debounce(loadOptions, 300) : undefined), [loadOptions]), ...commonProps, ...props }));
export { Select, StyledAsyncSelect, StyledCreatableSelect, StyledAsyncCreatableSelect };
